import styled from '@emotion/styled';
import React, { FC } from 'react';
import { useTableUniverse } from '~Components/TableUniverse';
import Icon from '~ComponentsImproved/Icon';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useCurrency, useI18n, useSelector, useStaffCan } from '~Hooks';
import { Box, TransparentButton } from '~Styles';
import CancelIcon from '~/Assets/icons/close.svg';
import PaymentMethod from '~Components/SplitPayment/Components/PaymentMethods/Method';
import PaymentStatus from '~Components/PaymentStatus';
import { handyBreakpoint } from '~Styles/breakpoints';

export const SplitPayModalWrapper = styled(Box)`
  position: relative;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  max-width: 600px;
  width: 100%;
  overflow: auto;
  padding: 60px 48px 40px;

  ${handyBreakpoint} {
    padding: 60px 24px 24px;
  }
`;

export const SplitPaymentMethodWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;

  ${handyBreakpoint} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const SplitPayCancelButton = styled(TransparentButton)`
  position: absolute;
  top: 0;
  left: 0;
  color: var(--colorBlueGray800);
  font-size: 16px;
  text-transform: none;
`;

interface Props {
  selectedChequeId?: string;
  onClose: () => void;
  initiatePayment: (methodId: string, customAmount?: number) => void;
}

const SplitPayment: FC<Props> = ({ selectedChequeId, onClose, initiatePayment }) => {
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const currency = useCurrency();
  const { i18n } = useI18n();
  const universe = useTableUniverse();
  const staffCan = useStaffCan();

  const order = useSelector(
    (state) => state.POSOrders.filter((o) => o.chequeId === selectedChequeId)[0] || null,
  );
  const payment = useSelector(
    (state) => state.POSPayments.filter((p) => order && p.order === order.id)[0] || null,
  );
  const cheque = universe.cheques.find((c) => c.id === selectedChequeId);

  const cancelButton = useButton({
    onClick: onClose,
  });

  const filteredPaymentMethods = paymentMethods
    .filter((method) => method.id !== 'INVOICE')
    .filter((method) => staffCan.canUsePaymentMethod(method.id));

  const startPayment = (amount: number, method: string) => {
    initiatePayment(method, amount);
  };

  if (!cheque) {
    return <></>;
  }

  const total = cheque.amount - cheque.discount;
  const alreadyPaid = cheque.paidAmount || 0;
  const amountRemaining = total - (cheque.paidAmount || 0);

  return (
    <SplitPayModalWrapper display="flex" flexDirection="column" gap="large">
      <SplitPayCancelButton display="flex" alignItems="center" {...cancelButton}>
        <Icon src={CancelIcon} height="32px" width="32px" color="var(--colorBlueGray800)" />
        {i18n('General.Cancel')}
      </SplitPayCancelButton>

      <Heading level={1} color="var(--colorWhite)">
        {i18n('SplitPayment.Title')}
      </Heading>

      <Box display="flex" flexDirection="column" gap="small">
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Paragraph level={4} color="var(--colorWhite)">
            {i18n('SplitPayment.OriginalTotal')}:
          </Paragraph>
          <Heading level={4} color="var(--colorWhite)">
            {currency.format(total)}
          </Heading>
        </Box>

        {Boolean(alreadyPaid) && (
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Paragraph level={4} color="var(--colorWhite)">
              {i18n('SplitPayment.AlreadyPaid')}:
            </Paragraph>{' '}
            <Heading level={4} color="var(--colorWhite)">
              {currency.format(alreadyPaid)}
            </Heading>
          </Box>
        )}

        {total !== amountRemaining && (
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Paragraph level={4} color="var(--colorWhite)">
              {i18n('SplitPayment.Remaining')}:
            </Paragraph>{' '}
            <Heading level={4} color="var(--colorWhite)">
              {currency.format(amountRemaining)}
            </Heading>
          </Box>
        )}

        {payment ? (
          <Box padding="large" marginTop="large">
            <PaymentStatus status={payment.status} paymentId={payment.id} />
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="center" marginTop="xs">
              <Paragraph level={4} color="var(--colorBlueGray400)" bottom={0}>
                {alreadyPaid ? i18n('SplitPayment.Desc.Next') : i18n('SplitPayment.Desc')}:
              </Paragraph>
            </Box>
            <SplitPaymentMethodWrapper>
              {...filteredPaymentMethods.map((method) => (
                <PaymentMethod
                  title={method.title}
                  numberInputTitle={i18n('SplitPayment.SelectAmount')}
                  method={method.id}
                  maxAmount={method.maxAmount}
                  amountRemaining={amountRemaining}
                  onStartPay={startPayment}
                  key={method.id}
                />
              ))}
            </SplitPaymentMethodWrapper>
          </>
        )}
      </Box>
    </SplitPayModalWrapper>
  );
};

export default SplitPayment;
